import { AttackSkill, StatusSkill } from "./skill";
import { DeltaAbilityLevel } from "./ablvl";
import { type Context, type Condition } from "./cond";
import { BattleStatus } from "./pet";
import { AbnormalState } from "./state";
import { Timing } from "./timing";
import { Buff, 神耀能量 } from "./buff";
import { reconcileSoulmarkEffects } from "./soulmarkSync";

/**
 * EffectResult 的数据结构或可选取值约定。
 */
export type EffectResult = boolean | number | void;

/**
 * ContextNumber 的数据结构或可选取值约定。
 */
export type ContextNumber = number | ((context: Context) => number);

/**
 * resolveContextNumber：执行对应的领域操作。
 * @param value 待处理的值
 * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
 * @returns 本次操作的结果；具体取值由当前状态与分支决定。
 */
function resolveContextNumber(
    value: ContextNumber | undefined,
    context: Context,
): number | undefined {
    return typeof value === "function" ? value(context) : value;
}

/** 解析效果本次使用的基数。 */
function resolveBaseNumber(
    base: ContextNumber,
    current: number | undefined,
    context: Context,
): number {
    return current ?? (typeof base === "function" ? base(context) : base);
}

/** 根据结算后的上下文推进基数，供下一次使用。 */
function advanceBaseNumber(
    value: number,
    step: ContextNumber | undefined,
    max: ContextNumber | undefined,
    context: Context,
): number | undefined {
    /**
     * stepValue，供本模块相关规则使用。
     */
    const stepValue = resolveContextNumber(step, context);
    /**
     * maxValue，供本模块相关规则使用。
     */
    const maxValue = resolveContextNumber(max, context);
    if (stepValue === undefined) {
        return maxValue === undefined ? undefined : Math.min(value, maxValue);
    }

    /**
     * next，供本模块相关规则使用。
     */
    const next = value + stepValue;
    return maxValue === undefined ? next : Math.min(next, maxValue);
}

/** 具体效果的业务实现，不参与技能流程调度。 */
export abstract class BaseEffect {
    /** 查询类效果只在对应规则真正应用时执行，不在普通阶段预先消耗。 */
    readonly queryOnly: boolean = false;
    /** 相对于效果持有者：自身行动或对方行动。 */
    readonly perspective: "self" | "opponent" = "self";
    /** 获取当前效果的说明文本。 */
    abstract get description(): string;

    /** 由 Effect 包装器调用的业务实现入口。 */
    describe(_context: Context): string {
        return this.description;
    }

    /**
     * apply：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    apply(context: Context): EffectResult {
        try {
            /**
             * result，供本模块相关规则使用。
             */
            const result = this.applyEffect(context);
            context.onEffectResult?.(this, result, context);
            return result;
        } finally {
            reconcileSoulmarkEffects(context);
        }
    }

    /** 具体效果主体实现。 */
    protected abstract applyEffect(context: Context): EffectResult;
}

/**
 * 效果调度包装器。
 *
 * 每个效果由 `cond` 决定是否尝试触发，由 `timing` 决定进入技能流程的
 * 阶段，由内部 BaseEffect 执行主体逻辑。主体结算后会立即按各子效果自己的
 * timing 选择性执行；子效果必须用 `Timing.triggered` 或
 * `Timing.untriggered`。
 */
export class Effect {
    /** 无副作用的存在条件，与本次触发条件分离。 */
    presenceCond?: Condition;
    source?: string;
    /** 持续声明是否授予对手，由领域同步解析目标。 */
    bindEnemy = false;

    withPresence(condition: Condition): this {
        this.presenceCond = condition;
        return this;
    }

    /**
     * prioritySource，保存当前实例的prioritySource状态。
     */
    prioritySource?: string;
    /** 默认只响应持有者；光环可显式监听己方或全场事件。 */
    listenScope: "owner" | "team" | "battle" = "owner";
    /** 是否允许存活替补响应事件（自身 battleBegin 例外）。 */
    allowStandby = false;
    /** 运行时来源标识，用于隔离魂印与技能以及不同声明的实例。 */
    soulmarkSource?: string;
    /** 领域同步生成的展示文本，UI 不执行条件或参数函数。 */
    displayDescription?: string;

    /**
     * listen：执行对应的领域操作。
     * @param scope scope参数，参与本次操作
     * @param allowStandby allowStandby参数，参与本次操作；默认值为 `false`
     * @returns 当前实例，便于链式调用。
     */
    listen(scope: "owner" | "team" | "battle", allowStandby = false): this {
        this.listenScope = scope;
        this.allowStandby = allowStandby;
        return this;
    }

    /** 只有当前技能的命中附效会受“命中效果失效”限制。 */
    hitEffect: boolean;
    /** 不参与调度的具体业务效果。 */
    readonly effect: BaseEffect;
    /** 当前效果的触发条件；未提供时默认始终满足。 */
    cond: Condition;
    /** 当前效果在技能流程中的触发时机；`undefined` 表示由父效果立即调用。 */
    timing: Timing | undefined;
    /** 当前效果结算后立即检查并执行的子效果。 */
    subEffects: Effect[];

    /**
     * 创建一个效果。
     *
     * @param effect 具体业务效果
     * @param timing 效果在技能流程中的生效时机；`undefined` 表示立即触发
     * @param cond 效果触发条件，未提供时默认始终满足
     * @param subEffects 当前效果完成后立即结算的子效果，所有子效果的 timing 必须为
     * `Timing.triggered` 或 `Timing.untriggered`
     */
    constructor(effect: BaseEffect, timing?: Timing, cond?: Condition, subEffects?: Effect[]) {
        if (!(effect instanceof BaseEffect)) {
            throw new TypeError("effect 必须是 BaseEffect 实例");
        }
        subEffects ??= [];
        if (subEffects.some((effect) => !(effect instanceof Effect))) {
            throw new TypeError("subEffects 必须只包含 Effect 实例");
        }
        if (
            subEffects.some(
                (effect) =>
                    effect.timing !== Timing.triggered && effect.timing !== Timing.untriggered,
            )
        ) {
            throw new Error("子效果的 timing 必须为 Timing.triggered 或 Timing.untriggered");
        }
        this.hitEffect =
            timing === Timing.onSkillHit || timing === Timing.onDamage || effect instanceof 秒杀;
        this.timing = timing;
        this.cond = cond ?? (() => true);
        this.subEffects = subEffects;
        this.effect = effect;
    }

    /** 获取内部业务效果的说明文本。 */
    get description(): string {
        return this.effect.description;
    }

    /**
     * 效果执行入口。
     *
     * 统一完成 `cond` 判定、主体逻辑执行和 `subEffects` 派发；具体效果
     * 通过内部 BaseEffect 实现主体逻辑。
     *
     * @param context 效果上下文
     * @returns 返回 `false` 表示本次效果未触发；返回 `true` 或 `undefined` 表示成功
     */
    onEffect(context: Context): boolean {
        if (typeof this.cond !== "function") {
            throw new TypeError(`效果 cond 必须是可调用的函数：${String(this.cond)}`);
        }
        /**
         * succeeded，供本模块相关规则使用。
         */
        const succeeded = this.cond(context) && this.applyEffect(context) !== false;
        for (const effect of this.subEffects) {
            /**
             * expectedTiming，供本模块相关规则使用。
             */
            const expectedTiming = succeeded ? Timing.triggered : Timing.untriggered;
            if (effect.timing !== expectedTiming) continue;
            effect.onEffect(context);
        }
        reconcileSoulmarkEffects(context);
        return succeeded;
    }

    /** 效果主体实现钩子；生命周期包装器会重写此方法。 */
    protected applyEffect(context: Context): EffectResult {
        return this.effect.apply(context);
    }
}

/** 持续若干回合、按内部效果时机重复结算的效果。 */
export class RoundEffect extends Effect {
    /**
     * activationDelay，保存当前实例的activationDelay状态。
     */
    activationDelay = 0;
    /**
     * activeFromRound，保存当前实例的activeFromRound状态。
     */
    activeFromRound = 0;
    /**
     * lifetime，保存当前实例的lifetime状态。
     */
    lifetime: "actions" | "round" = "actions";

    /**
     * nextRound：执行对应的领域操作。
     * @returns 当前实例，便于链式调用。
     */
    nextRound(): this {
        this.activationDelay = 1;
        return this;
    }

    /**
     * thisRound：执行对应的领域操作。
     * @returns 当前实例，便于链式调用。
     */
    thisRound(): this {
        this.lifetime = "round";
        return this;
    }
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "持续若干回合、按内部效果时机重复结算。";
    }

    /**
     * 总回合数
     */
    totalRoundNum: number;
    /**
     * 剩余回合数
     */
    remainRoundNum: number;
    /**
     * 每回合生效的效果
     */
    readonly effect: BaseEffect;
    /** 每回合执行内部效果的时机。 */
    effectTiming: Timing | undefined;
    /** 每回合执行内部效果的条件。 */
    effectCond: Condition;
    /**
     * 效果是否绑定敌方
     */
    bindEnemy: boolean;

    /**
     * 创建一个持续回合效果。
     *
     * @param timing 回合类效果的注册时机
     * @param roundNum 回合数
     * @param effect 每回合生效的业务效果
     * @param effectTiming 每回合生效时机；未指定时无法注册
     * @param effectCond 每回合生效条件
     * @param bindEnemy 效果是否绑定敌方，若指定为 true 则回合类效果绑定敌方
     * @param subEffects 注册成功或失败后立即检查的子效果
     */
    constructor(
        timing: Timing | undefined,
        roundNum: number,
        effect: BaseEffect,
        effectTiming?: Timing,
        effectCond?: Condition,
        bindEnemy: boolean = false,
        subEffects: Effect[] = [],
        cond?: Condition,
    ) {
        super(effect, timing, cond, subEffects);
        this.totalRoundNum = this.remainRoundNum = roundNum;
        this.effect = effect;
        this.effectTiming = effectTiming;
        this.effectCond = effectCond ?? (() => true);
        this.bindEnemy = bindEnemy;
    }

    /**
     * 回合类效果【注册】生效
     * @param sbjPet 发起注册的精灵
     * @param objPet 目标精灵
     * @returns 是否成功注册
     */
    protected applyEffect({ sbjPet, objPet, skill }: Context) {
        this.prioritySource ??= skill?.name ?? this.soulmarkSource;
        this.reset();
        if (this.bindEnemy) {
            return objPet.registerRoundEffect(this);
        } else {
            return sbjPet.registerRoundEffect(this);
        }
    }

    /**
     * 回合类效果【每回合】生效
     * @param context 效果上下文
     * @returns 本次回合结算是否完成
     */
    onRoundEffect(context: Context) {
        if (this.remainRoundNum <= 0 || context.sbjPet.roundNumber < this.activeFromRound)
            return false;
        return this.effectCond(context) && this.effect.apply(context) !== false;
    }

    /**
     * 重置相应的回合类效果
     */
    reset() {
        this.remainRoundNum = this.totalRoundNum;
    }

    /**
     * 回合类效果生效后，经过一个回合
     */
    pass() {
        this.remainRoundNum = Math.max(0, this.remainRoundNum - 1);
    }
}

/** 触发指定次数、每次消耗一次次数的效果。 */
export class ConsumeEffect extends Effect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "触发指定次数，每次触发消耗一次次数。";
    }

    /**
     * 总触发次数
     */
    totalTriggerNum: number;
    /**
     * 剩余触发次数
     */
    remainTriggerNum: number;
    /**
     * 每次触发时生效的效果
     */
    readonly effect: BaseEffect;
    /** 每次执行内部效果的时机。 */
    effectTiming: Timing | undefined;
    /** 每次执行内部效果的条件。 */
    effectCond: Condition;
    /**
     * 效果是否绑定敌方
     */
    bindEnemy: boolean;

    /**
     * 创建一个消耗类效果。
     *
     * @param timing 消耗类效果的注册时机
     * @param triggerNum 总触发次数
     * @param effect 每次触发时生效的业务效果
     * @param effectTiming 每次触发生效时机；未指定时无法注册
     * @param effectCond 每次触发生效条件
     * @param bindEnemy 效果是否绑定敌方，若指定为 true 则消耗类效果绑定敌方
     * @param subEffects 注册成功或失败后立即检查的子效果
     */
    constructor(
        timing: Timing | undefined,
        triggerNum: number,
        effect: BaseEffect,
        effectTiming?: Timing,
        effectCond?: Condition,
        bindEnemy: boolean = false,
        subEffects: Effect[] = [],
        cond?: Condition,
    ) {
        super(effect, timing, cond, subEffects);
        this.totalTriggerNum = this.remainTriggerNum = triggerNum;
        this.effect = effect;
        this.effectTiming = effectTiming;
        this.effectCond = effectCond ?? (() => true);
        this.bindEnemy = bindEnemy;
    }

    /**
     * 消耗类效果注册生效
     */
    protected applyEffect({ sbjPet, objPet, skill }: Context) {
        this.prioritySource ??= skill?.name ?? this.soulmarkSource;
        this.reset();
        if (this.bindEnemy) {
            return objPet.registerConsumeEffect(this);
        } else {
            return sbjPet.registerConsumeEffect(this);
        }
    }

    /**
     * 消耗类效果触发生效
     * @param context 效果上下文
     * @returns 是否仍有剩余次数并完成本次触发
     */
    onConsumeEffect(context: Context) {
        if (this.remainTriggerNum <= 0) {
            return false;
        }
        if (!this.effectCond(context) || this.effect.apply(context) === false) return false;
        this.consume();
        return true;
    }

    /**
     * 重置剩余触发次数
     */
    reset() {
        this.remainTriggerNum = this.totalTriggerNum;
    }

    /**
     * 消耗一次触发次数
     */
    consume() {
        this.remainTriggerNum = Math.max(0, this.remainTriggerNum - 1);
    }
}

/** 增加指定 Buff 层数；Buff 不存在时创建一个独立实例。 */
export class 获得Buff层数<T extends Buff> extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "增加指定 Buff 层数；Buff 不存在时创建一个独立实例。";
    }

    /**
     * 创建实例并初始化所需的领域状态。
     * @param buffType Buff 类型
     * @param stacks 层数
     */
    constructor(buffType: new () => T, stacks: number) {
        super();
        this.buffType = buffType;
        this.stacks = stacks;
    }

    /**
     * buffType，保存当前实例的buffType状态。
     */
    private readonly buffType: new () => T;
    /**
     * stacks，保存当前实例的stacks状态。
     */
    private readonly stacks: number;

    /**
     * applyEffect：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect(context: Context) {
        return context.sbjPet.addBuffStacks(this.buffType, this.stacks, context) > 0;
    }
}

/** 重生之翼获得固定层数的神耀能量。 */
export class 获得x层神耀能量 extends 获得Buff层数<神耀能量> {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "获得指定层数的神耀能量。";
    }

    /**
     * 创建实例并初始化所需的领域状态。
     * @param stacks 层数
     */
    constructor(stacks: number) {
        super(神耀能量, stacks);
    }
}

/** 银雾之翼根据本次攻击是否暴击获得 2/1 层神耀能量。 */
export class 根据暴击获得神耀能量 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "根据本次攻击是否暴击获得神耀能量。";
    }

    /**
     * applyEffect：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect(context: Context) {
        /**
         * stacks，供本模块相关规则使用。
         */
        const stacks = context.sbjPet.criticalStrike ? 2 : 1;
        return context.sbjPet.addBuffStacks(神耀能量, stacks, context) > 0;
    }
}

/** 让击败效果在限定回合内保留指定神耀能量层数。 */
export class 击败后保留x层神耀能量 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "击败后在限定回合内保留指定层数的神耀能量。";
    }

    /**
     * 创建实例并初始化所需的领域状态。
     * @param stacks 层数
     * @param rounds 持续回合数
     */
    constructor(stacks: number, rounds: number) {
        super();
        this.stacks = stacks;
        this.rounds = rounds;
    }

    /**
     * stacks，保存当前实例的stacks状态。
     */
    private readonly stacks: number;
    /**
     * rounds，保存当前实例的rounds状态。
     */
    private readonly rounds: number;

    /**
     * applyEffect：执行对应的领域操作。
     * @param { sbjPet } { sbjPet }参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect({ sbjPet }: Context) {
        /**
         * energy，供本模块相关规则使用。
         */
        const energy = sbjPet.getBuff(神耀能量);
        if (!energy) return false;
        energy.grantDefeatRetention(this.stacks, this.rounds);
        return true;
    }
}

/** 正义天启歌按神耀能量层数增加技能基础威力。 */
export class 每层神耀能量增加技能威力x点 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "按神耀能量层数增加技能基础威力。";
    }

    /**
     * 创建实例并初始化所需的领域状态。
     * @param power 每层增加的威力
     */
    constructor(power: number) {
        super();
        this.power = power;
    }

    /**
     * power，保存当前实例的power状态。
     */
    private readonly power: number;

    /**
     * applyEffect：执行对应的领域操作。
     * @param { sbjPet } { sbjPet }参数，参与本次操作
     */
    protected applyEffect({ sbjPet }: Context) {
        /**
         * addedPower，供本模块相关规则使用。
         */
        const addedPower = (sbjPet.getBuff(神耀能量)?.stacks ?? 0) * this.power;
        /**
         * previous，供本模块相关规则使用。
         */
        const previous = sbjPet.powerChange;
        sbjPet.powerChange = (power) => previous(power) + addedPower;
    }
}

/** 魂印 4 层效果：回合结束反馈本回合免疫的固定伤害。 */
export class 神耀能量固定伤害反馈 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "回合结束时反馈本回合免疫的固定伤害。";
    }

    /**
     * applyEffect：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect(context: Context) {
        /**
         * { sbjPet, objPet }，供本模块相关规则使用。
         */
        const { sbjPet, objPet } = context;
        /**
         * damage，供本模块相关规则使用。
         */
        const damage = sbjPet.consumeBlockedFixedDamage();
        if (damage <= 0) return false;
        objPet.战斗面板.fixedDamage = sbjPet.causeFixedDamage(damage, objPet, 0, 1, context);
        return true;
    }
}

/** 击败对手后消耗神耀能量，并记录下回合的先制与吸取奖励。 */
export class 消耗神耀能量并获得击败奖励 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "击败对手后消耗神耀能量并获得下回合奖励。";
    }

    /**
     * applyEffect：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect(context: Context) {
        /**
         * { sbjPet }，供本模块相关规则使用。
         */
        const { sbjPet } = context;
        /**
         * energy，供本模块相关规则使用。
         */
        const energy = sbjPet.getBuff(神耀能量);
        if (!energy || energy.stacks <= 0) return false;
        /**
         * retained，供本模块相关规则使用。
         */
        const retained =
            energy.defeatRetentionRounds > 0 ? Math.min(energy.stacks, energy.defeatRetention) : 0;
        /**
         * consumed，供本模块相关规则使用。
         */
        const consumed = energy.stacks - retained;
        energy.层数 = retained;
        energy.grantDefeatRetention(0, 0);
        sbjPet.refreshBuffEffects(energy, context);
        if (consumed > 0) {
            sbjPet.registerRoundEffect(new RoundEffect(undefined, 1, new 先制(consumed), Timing.battleStageBegin).nextRound());
            sbjPet.addNextRoundReward(consumed * 0.08);
        }
        return consumed > 0;
    }
}

/** 随机复活己方背包中一只已战败的其他精灵。 */
export class 随机复活一只已战败的其他精灵 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "随机复活己方背包中一只已战败的其他精灵。";
    }

    /**
     * applyEffect：执行对应的领域操作。
     * @param { sbjPet, sbjSeer } { sbjPet, sbjSeer }参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect({ sbjPet, sbjSeer }: Context) {
        /**
         * candidates，供本模块相关规则使用。
         */
        const candidates = sbjSeer.petPackage.filter(
            (pet) => pet !== sbjPet && pet.战斗状态 === BattleStatus.DEAD && !pet.isAlive(),
        );
        if (candidates.length === 0) return false;
        /**
         * selected，供本模块相关规则使用。
         */
        const selected = candidates[Math.floor(sbjSeer.random.next() * candidates.length)];
        selected.revive();
        return true;
    }
}

/**
 * 恢复体力：封装效果声明、生命周期包装与具体业务效果。
 */
export class 恢复体力 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "恢复自身最大体力的一定比例。";
    }

    /**
     * 恢复体力的基数，若为函数则按上下文计算
     */
    private base: number | ((context: Context) => number);
    /**
     * 恢复体力的步长，若为函数则按上下文计算
     */
    private step?: number | ((context: Context) => number);
    /**
     * 恢复体力的上限，若为函数则按上下文计算
     */
    private max?: number | ((context: Context) => number);
    /** 已按步长推进后的基数；不配置步长时保持未设置。 */
    private currentBase?: number;
    /**
     * 恢复体力翻倍的条件，若满足则恢复效果翻倍
     */
    private doubleCond?: Condition;

    /**
     * 构造恢复体力效果。
     *
     * @param base 恢复体力的基数，若为函数则按上下文计算
     * @param step 恢复体力的步长，若为函数则按上下文计算
     * @param max 恢复体力的上限，若为函数则按上下文计算
     * @param doubleCond 恢复体力翻倍的条件，若满足则恢复效果翻倍
     */
    constructor(
        base: number | ((context: Context) => number),
        step?: number | ((context: Context) => number),
        max?: number | ((context: Context) => number),
        doubleCond?: Condition,
    ) {
        super();
        this.base = base;
        this.step = step;
        this.max = max;
        this.doubleCond = doubleCond;
    }

    /**
     * applyEffect：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     */
    protected applyEffect(context: Context) {
        /**
         * { sbjPet }，供本模块相关规则使用。
         */
        const { sbjPet } = context;
        /**
         * base，供本模块相关规则使用。
         */
        const base = resolveBaseNumber(this.base, this.currentBase, context);
        /**
         * recoverHP，供本模块相关规则使用。
         */
        let recoverHP = base;

        if (this.doubleCond && this.doubleCond(context)) {
            recoverHP *= 2;
        }

        sbjPet.战斗面板.recoverHP = sbjPet.recover(recoverHP);
        this.currentBase = advanceBaseNumber(base, this.step, this.max, context);
    }
}

/** 恢复自身最大体力一定比例，并可按条件造成等量固定伤害。 */
export class 回血·固伤 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "恢复自身最大体力的一定比例。";
    }

    /** 恢复体力的基数，若为函数则按上下文计算 */
    private base: number | ((context: Context) => number);
    /** 恢复体力的步长，若为函数则按上下文计算 */
    private step?: number | ((context: Context) => number);
    /** 恢复体力的上限，若为函数则按上下文计算 */
    private max?: number | ((context: Context) => number);
    /** 已按步长推进后的基数；不配置步长时保持未设置。 */
    private currentBase?: number;
    /** 恢复体力翻倍的条件，若满足则恢复效果翻倍 */
    private doubleCond?: Condition;

    /**
     * 构造恢复体力效果。
     *
     * @param base 恢复体力的基数，若为函数则按上下文计算
     * @param step 恢复体力的步长，若为函数则按上下文计算
     * @param max 恢复体力的上限，若为函数则按上下文计算
     * @param doubleCond 恢复体力翻倍的条件，若满足则恢复效果翻倍
     */
    constructor(
        base: number | ((context: Context) => number),
        step?: number | ((context: Context) => number),
        max?: number | ((context: Context) => number),
        doubleCond?: Condition,
    ) {
        super();
        this.base = base;
        this.step = step;
        this.max = max;
        this.doubleCond = doubleCond;
    }

    /**
     * applyEffect：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     */
    protected applyEffect(context: Context) {
        /**
         * { sbjPet, objPet }，供本模块相关规则使用。
         */
        const { sbjPet, objPet } = context;
        /**
         * base，供本模块相关规则使用。
         */
        let base = resolveBaseNumber(this.base, this.currentBase, context);

        /**
         * recoverHP，供本模块相关规则使用。
         */
        let recoverHP = base;
        if (this.doubleCond && this.doubleCond(context)) {
            recoverHP *= 2;
        }

        sbjPet.战斗面板.recoverHP = sbjPet.recover(recoverHP);
        objPet.战斗面板.fixedDamage = sbjPet.causeFixedDamage(
            sbjPet.战斗面板.recoverHP,
            objPet,
            0,
            1,
            context,
        );
        this.currentBase = advanceBaseNumber(base, this.step, this.max, context);
    }
}

/** 附加自身最大体力百分比的固定伤害，并可选择恢复等量体力。 */
export class 固伤·回血 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "附加自身最大体力一定百分比的固定伤害。";
    }

    /**
     * base，保存当前实例的base状态。
     */
    private base: number | ((context: Context) => number);
    /**
     * step，保存当前实例的step状态。
     */
    private step?: number | ((context: Context) => number);
    /**
     * max，保存当前实例的max状态。
     */
    private max?: number | ((context: Context) => number);
    /** 已按步长推进后的基数；不配置步长时保持未设置。 */
    private currentBase?: number;
    /**
     * doubleCond，保存当前实例的doubleCond状态。
     */
    private doubleCond?: Condition;

    /**
     * 构造固定伤害效果。
     *
     * @param base 固定伤害基数，若为函数则按上下文计算
     * @param step 每次使用的增量，若为函数则按上下文计算
     * @param max 固定伤害上限，若为函数则按上下文计算
     * @param doubleCond 固定伤害翻倍的条件
     */
    constructor(
        base: number | ((context: Context) => number),
        step?: number | ((context: Context) => number),
        max?: number | ((context: Context) => number),
        doubleCond?: Condition,
    ) {
        super();
        this.base = base;
        this.step = step;
        this.max = max;
        this.doubleCond = doubleCond;
    }

    /**
     * applyEffect：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     */
    protected applyEffect(context: Context) {
        /**
         * { sbjPet, objPet }，供本模块相关规则使用。
         */
        const { sbjPet, objPet } = context;
        /**
         * base，供本模块相关规则使用。
         */
        const base = resolveBaseNumber(this.base, this.currentBase, context);
        /**
         * fixedDamage，供本模块相关规则使用。
         */
        let fixedDamage = typeof this.base === "function" ? base : Math.round(base);

        if (this.doubleCond && this.doubleCond(context)) {
            fixedDamage *= 2;
        }

        /**
         * recoverHP，供本模块相关规则使用。
         */
        let recoverHP = fixedDamage;

        fixedDamage = sbjPet.causeFixedDamage(fixedDamage, objPet, 0, 1, context);
        recoverHP = sbjPet.recover(recoverHP);

        objPet.战斗面板.fixedDamage = fixedDamage;
        sbjPet.战斗面板.recoverHP = recoverHP;
        this.currentBase = advanceBaseNumber(base, this.step, this.max, context);
    }
}

/** 吸取对手一定体力并恢复自身等量体力。 */
export class 吸取体力 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "吸取对手一定体力并恢复自身等量体力。";
    }

    /** 吸取体力的基数，若为函数则按上下文计算 */
    private base: number | ((context: Context) => number);
    /** 吸取体力的步长，若为函数则按上下文计算 */
    private step?: number | ((context: Context) => number);
    /** 吸取体力的上限，若为函数则按上下文计算 */
    private max?: number | ((context: Context) => number);
    /** 已按步长推进后的基数；不配置步长时保持未设置。 */
    private currentBase?: number;
    /** 吸取效果翻倍的条件，若满足则吸取效果翻倍 */
    private doubleCond?: Condition;

    /**
     * 构造吸取体力效果。
     *
     * @param base 吸取体力的基数；为数值时按固定伤害结算，为函数时按上下文计算并按百分比伤害结算
     * @param step 每次使用的增量，若为函数则按上下文计算
     * @param max 吸取体力的上限，若为函数则按上下文计算
     * @param doubleCond 吸取效果翻倍的条件
     */
    constructor(
        base: number | ((context: Context) => number),
        step?: number | ((context: Context) => number),
        max?: number | ((context: Context) => number),
        doubleCond?: Condition,
    ) {
        super();
        this.base = base;
        this.step = step;
        this.max = max;
        this.doubleCond = doubleCond;
    }

    /**
     * applyEffect：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     */
    protected applyEffect(context: Context) {
        /**
         * { sbjPet, objPet }，供本模块相关规则使用。
         */
        const { sbjPet, objPet } = context;
        /**
         * base，供本模块相关规则使用。
         */
        const base = resolveBaseNumber(this.base, this.currentBase, context);
        // 函数基数表示按对手最大体力比例吸取，取整后按百分比伤害结算
        /**
         * percent，供本模块相关规则使用。
         */
        const percent = typeof this.base === "function";
        /**
         * absorb，供本模块相关规则使用。
         */
        let absorb = percent ? Math.round(base) : base;

        if (this.doubleCond && this.doubleCond(context)) {
            absorb *= 2;
        }

        /**
         * recoverHP，供本模块相关规则使用。
         */
        const recoverHP = sbjPet.recover(absorb);
        absorb = percent
            ? sbjPet.causePercentDamage(absorb, objPet)
            : sbjPet.causeFixedDamage(absorb, objPet, 0, 1, context);

        if (percent) {
            objPet.战斗面板.percentDamage = absorb;
        } else {
            objPet.战斗面板.fixedDamage = absorb;
        }
        sbjPet.战斗面板.recoverHP = recoverHP;
        this.currentBase = advanceBaseNumber(base, this.step, this.max, context);
    }
}

/** 清除对手当前所有能力强化效果。 */
export class 消除强化 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "清除对手当前所有能力强化效果。";
    }

    /**
     * @param timing 效果触发时机
     * @param subEffects 清除成功或失败后按谓词选择的子效果
     * @param cond 当前效果的触发条件
     */
    protected applyEffect({ objPet }: Context) {
        return objPet.能力等级.isLevelUp() && objPet.能力等级.clearLevelUp();
    }
}

/** 清除对手当前拥有的全部回合类效果。 */
export class 消除回合 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "清除对手当前拥有的全部回合类效果。";
    }

    /**
     * @param timing 效果触发时机
     * @param subEffects 清除成功或失败后按谓词选择的子效果
     * @param cond 当前效果的触发条件
     */
    protected applyEffect({ objPet }: Context) {
        return objPet.destroyRoundEffect();
    }
}

/**
 * 吞噬强化：封装效果声明、生命周期包装与具体业务效果。
 */
export class 吞噬强化 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "消除对手当前所有能力强化效果，并将其转化为自身能力强化。";
    }
    
    /**
     * applyEffect：执行对应的领域操作。
     * @param { sbjPet, objPet } { sbjPet, objPet }参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect({ sbjPet, objPet }: Context) {
        sbjPet.能力等级.change({
            pAtk: objPet.能力等级.pAtk > 0 ? objPet.能力等级.pAtk : 0,
            pDef: objPet.能力等级.pDef > 0 ? objPet.能力等级.pDef : 0,
            mAtk: objPet.能力等级.mAtk > 0 ? objPet.能力等级.mAtk : 0,
            mDef: objPet.能力等级.mDef > 0 ? objPet.能力等级.mDef : 0,
            speed: objPet.能力等级.speed > 0 ? objPet.能力等级.speed : 0,
            hit: objPet.能力等级.hit > 0 ? objPet.能力等级.hit : 0,
        });
        return objPet.能力等级.clearLevelUp();
    }
}

/** 提升自身全部能力等级。 */
export class 强化 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "提升自身全部能力等级。";
    }

    /**
     * lv，保存当前实例的lv状态。
     */
    private lv: number;
    /**
     * doubleCond，保存当前实例的doubleCond状态。
     */
    private doubleCond?: Condition;

    /**
     * 构造全属性提升效果。
     *
     * @param lv 提升等级，默认为1
     * @param doubleCond 提升等级翻倍的条件
     */
    constructor(lv?: number, doubleCond?: Condition) {
        super();
        this.lv = lv ?? 1;
        this.doubleCond = doubleCond;
    }

    /**
     * applyEffect：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect(context: Context) {
        /**
         * { sbjPet }，供本模块相关规则使用。
         */
        const { sbjPet } = context;
        if (this.doubleCond && this.doubleCond(context)) {
            sbjPet.能力等级.allLevelUp(this.lv * 2);
        } else {
            sbjPet.能力等级.allLevelUp(this.lv);
        }
        return true;
    }
}

/** 降低对手全部能力等级。 */
export class 弱化 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "降低对手全部能力等级。";
    }

    /**
     * lv，保存当前实例的lv状态。
     */
    private lv: number;
    /**
     * doubleCond，保存当前实例的doubleCond状态。
     */
    private doubleCond?: Condition;

    /**
     * 构造对手全属性降低效果。
     *
     * @param lv 降低等级，默认为1
     * @param doubleCond 降低等级翻倍的条件
     */
    constructor(lv?: number, doubleCond?: Condition) {
        super();
        this.lv = lv ?? 1;
        this.doubleCond = doubleCond;
    }

    /**
     * applyEffect：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect(context: Context) {
        /**
         * { objPet }，供本模块相关规则使用。
         */
        const { objPet } = context;
        if (this.doubleCond && this.doubleCond(context)) {
            return objPet.lowerAllAbilityLevels(this.lv * 2, context);
        } else {
            return objPet.lowerAllAbilityLevels(this.lv, context);
        }
    }
}

/** 按给定概率尝试直接令对手失去战斗能力。 */
export class 秒杀 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "按给定概率尝试直接令对手失去战斗能力。";
    }

    /**
     * isTriggered，保存当前实例的isTriggered状态。
     */
    public isTriggered: boolean = false;
    /**
     * prop，保存当前实例的prop状态。
     */
    private readonly prop: number;

    /**
     * 构造概率秒杀效果。
     *
     * @param prop 百分之 prop 的秒杀概率
     */
    constructor(prop: number = 100) {
        super();
        if (!Number.isFinite(prop) || prop < 0 || prop > 100) {
            throw new RangeError("秒杀概率必须是 0 到 100 之间的有限数值");
        }
        this.prop = prop;
    }

    /**
     * applyEffect：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect(context: Context) {
        // 每次结算都重新判定，确保标志位不会残留上一次的触发结果
        this.isTriggered = context.sbjSeer.random.next() < this.prop / 100;
        if (this.isTriggered && context.action) context.action.instakill = true;
        return this.isTriggered;
    }
}

/** 将本次直接攻击标记为必定致命一击。 */
export class 暴击 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "使本次直接攻击必定致命一击。";
    }

    /**
     * applyEffect：执行对应的领域操作。
     * @param { sbjPet } { sbjPet }参数，参与本次操作
     */
    protected applyEffect({ sbjPet }: Context) {
        sbjPet.criticalStrike = true;
    }
}

/** 按百分比提高本次普通攻击造成的伤害，由伤害结算读取返回值。 */
export class 增伤 extends BaseEffect {
    /**
     * 创建实例并初始化所需的领域状态。
     * @param percent percent参数，参与本次操作
     */
    constructor(public readonly percent: ContextNumber) {
        super();
        if (typeof percent === "number") this.validate(percent);
    }

    /**
     * validate：执行对应的领域操作。
     * @param percent percent参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    private validate(percent: number) {
        if (!Number.isFinite(percent) || percent < 0)
            throw new RangeError("增伤百分比必须是非负有限数值");
        return percent;
    }

    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description() {
        return typeof this.percent === "number"
            ? `造成的普通攻击伤害提高${this.percent}%`
            : "按当前条件提高普通攻击伤害。";
    }

    /**
     * describe：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    override describe(context: Context) {
        return `造成的普通攻击伤害提高${resolveContextNumber(this.percent, context)}%`;
    }

    /**
     * applyEffect：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect(context: Context) {
        return this.validate(resolveContextNumber(this.percent, context)!);
    }
}

/** 附加指定数值的固定伤害。 */
export class 固伤 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "附加指定数值的固定伤害。";
    }

    /**
     * 基础伤害数值
     */
    private base: number | ((context: Context) => number);
    /**
     * 每次使用的增量
     */
    private step?: number | ((context: Context) => number);
    /**
     * 最大伤害数值
     */
    private max?: number | ((context: Context) => number);
    /** 已按步长推进后的基数；不配置步长时保持未设置。 */
    private currentBase?: number;

    /**
     * @param base 基础伤害数值
     * @param step 每次使用的增量
     * @param max 最大伤害数值
     */
    constructor(
        base: number | ((context: Context) => number),
        step?: number | ((context: Context) => number),
        max?: number | ((context: Context) => number),
    ) {
        super();
        this.base = base;
        this.step = step;
        this.max = max;
    }

    /**
     * applyEffect：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     */
    protected applyEffect(context: Context) {
        /**
         * base，供本模块相关规则使用。
         */
        const base = resolveBaseNumber(this.base, this.currentBase, context);
        /**
         * fixedDamage，供本模块相关规则使用。
         */
        const fixedDamage = base;

        /**
         * { sbjPet, objPet }，供本模块相关规则使用。
         */
        const { sbjPet, objPet } = context;
        if (objPet.战斗面板.fixedDamage) {
            objPet.战斗面板.fixedDamage += sbjPet.causeFixedDamage(
                fixedDamage,
                objPet,
                0,
                1,
                context,
            );
        } else {
            objPet.战斗面板.fixedDamage = sbjPet.causeFixedDamage(
                fixedDamage,
                objPet,
                0,
                1,
                context,
            );
        }
        this.currentBase = advanceBaseNumber(base, this.step, this.max, context);
    }
}

/** 按给定变化量提升自身能力等级。 */
export class 属性提升 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "按给定变化量提升自身能力等级。";
    }

    /**
     * delta，保存当前实例的delta状态。
     */
    private delta: DeltaAbilityLevel;

    /**
     * 自身属性提升。
     * @param delta 能力等级变化量
     * @param timing 效果触发时机
     * @param cond 当前效果的触发条件
     * @param subEffects 当前效果结算后的子效果
     */
    constructor(delta: DeltaAbilityLevel) {
        super();
        this.delta = delta;
    }

    /**
     * applyEffect：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     */
    protected applyEffect(context: Context) {
        context.sbjPet.changeAbilityLevel(this.delta, context);
        return;
    }
}

/** 按给定变化量降低对手能力等级。 */
export class 属性下降 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "按给定变化量降低对手能力等级。";
    }

    /**
     * delta，保存当前实例的delta状态。
     */
    private delta: DeltaAbilityLevel;

    /**
     * 对手属性下降。
     * @param delta 能力等级变化量
     * @param timing 效果触发时机
     * @param cond 当前效果的触发条件
     * @param subEffects 当前效果结算后的子效果
     */
    constructor(delta: DeltaAbilityLevel) {
        super();
        this.delta = delta;
    }

    /**
     * applyEffect：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     */
    protected applyEffect(context: Context) {
        /**
         * { objPet }，供本模块相关规则使用。
         */
        const { objPet } = context;
        /**
         * delta，供本模块相关规则使用。
         */
        const delta = Object.fromEntries(
            Object.entries(this.delta).map(([key, value]) => [key, -Math.abs(value ?? 0)]),
        ) as DeltaAbilityLevel;
        objPet.changeAbilityLevel(delta, context);
    }
}

/** 使对手进入指定异常状态，未指定时长时随机持续1～3回合。 */
export class 施加异常状态 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return `使对手${this.abState.name}`;
    }

    /**
     * 要施加的异常状态
     */
    private abState: AbnormalState;
    /**
     * roundNum，保存当前实例的roundNum状态。
     */
    private roundNum?: number;

    /**
     * 构造异常状态施加效果。
     *
     * @param abState 要施加的异常状态
     * @param roundNum 持续回合数，优先于状态自带时长
     */
    constructor(abState: AbnormalState, roundNum?: number) {
        super();
        this.abState = abState;
        this.roundNum = roundNum;
    }

    /**
     * applyEffect：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect(context: Context) {
        const { objPet, sbjSeer } = context;
        if (objPet.immuneAbnormal || objPet.queryProtection(免疫异常状态, context)) return false;
        const rate = objPet.异常抗性.find(item => item.state === this.abState.name)?.rate ?? 0;
        if (rate > 0 && sbjSeer.random.next() < rate) return false;
        const roundNum = this.roundNum ?? (this.abState.randomDuration
            ? 1 + Math.floor(sbjSeer.random.next() * 3)
            : this.abState.totalRoundNum);
        return objPet.fallIntoState(this.abState, roundNum);
    }
}

/** 令指定一方暂时无法使用属性技能。 */
export class 属性封印 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "令指定一方暂时无法使用属性技能。";
    }

    /**
     * @param timing 效果触发时机
     * @param cond 触发条件
     * @param subEffects 当前效果结算后的子效果
     */
    readonly perspective = "self" as const;

    /**
     * applyEffect：执行对应的领域操作。
     * @param { action, skill } { action, skill }参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect({ action, skill }: Context) {
        if (!action || !(skill instanceof StatusSkill)) return false;
        action.sealed = true;
        return true;
    }
}

/** 按百分比提高当前技能威力。 */
export class 威力提升 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description(): string {
        return "按百分比提高当前技能威力。";
    }

    /**
     * percent，保存当前实例的percent状态。
     */
    private percent: number;

    /**
     * 构造技能威力增幅效果。
     *
     * @param percent 百分之x
     */
    constructor(percent: number) {
        super();
        this.percent = percent;
    }

    /**
     * applyEffect：执行对应的领域操作。
     * @param { sbjPet } { sbjPet }参数，参与本次操作
     */
    protected applyEffect({ sbjPet }: Context) {
        /**
         * previous，供本模块相关规则使用。
         */
        const previous = sbjPet.powerChange;
        sbjPet.powerChange = (power: number) => previous(power) * (1 + this.percent / 100);
    }
}

/** 师盔在命中前阻止攻击技能，且只响应对方行动。 */
export class 免疫攻击 extends BaseEffect {
    /**
     * perspective，保存当前实例的perspective状态。
     */
    readonly perspective = "opponent" as const;
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description() {
        return "免疫对手的攻击技能。";
    }
    /**
     * applyEffect：执行对应的领域操作。
     * @param { action, skill } { action, skill }参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect({ action, skill }: Context) {
        if (
            !action ||
            !(skill instanceof AttackSkill) ||
            action.attackImmune ||
            action.ignoreAttackImmunity
        )
            return false;
        action.attackImmune = true;
        return true;
    }
}

/**
 * 无视攻击免疫：封装效果声明、生命周期包装与具体业务效果。
 */
export class 无视攻击免疫 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description() {
        return "无视师盔。";
    }
    /**
     * applyEffect：执行对应的领域操作。
     * @param { action } { action }参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect({ action }: Context) {
        if (!action) return false;
        action.ignoreAttackImmunity = true;
        return true;
    }
}

/** 持续保护由 Pet 在异常附加时查询，不依赖上一次行动的临时标志。 */
export class 免疫异常状态 extends BaseEffect {
    /**
     * queryOnly，保存当前实例的queryOnly状态。
     */
    readonly queryOnly = true;
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description() {
        return "免疫异常状态。";
    }
    /**
     * applyEffect：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect() {
        return true;
    }
}

/** 在能力下降实际应用时查询，支持常驻、回合和次数包装器。 */
export class 免疫能力下降 extends BaseEffect {
    /**
     * queryOnly，保存当前实例的queryOnly状态。
     */
    readonly queryOnly = true;
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description() {
        return "免疫能力下降。";
    }
    /**
     * applyEffect：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect() {
        return true;
    }
}

/** 在固定伤害实际应用时查询，并由 Pet 记录被阻止的固定伤害。 */
export class 免疫固定伤害 extends BaseEffect {
    /**
     * queryOnly，保存当前实例的queryOnly状态。
     */
    readonly queryOnly = true;
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description() {
        return "免疫固定伤害。";
    }
    /**
     * applyEffect：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect() {
        return true;
    }
}

/** 排序读取先制时计算，不改变下一回合奖励或临时先制的剩余时间。 */
export class 先制 extends BaseEffect {
    /**
     * queryOnly，保存当前实例的queryOnly状态。
     */
    readonly queryOnly = true;
    /**
     * 创建实例并初始化所需的领域状态。
     * @param bonus bonus参数，参与本次操作
     */
    constructor(public readonly bonus: number) {
        super();
    }
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description() {
        return `技能额外先制+${this.bonus}。`;
    }
    /**
     * applyEffect：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     */
    protected applyEffect(context: Context) {
        context.priorityBonus = (context.priorityBonus ?? 0) + this.bonus;
    }
}

/**
 * 技能封印：封装效果声明、生命周期包装与具体业务效果。
 */
export class 技能封印 extends BaseEffect {
    /**
     * 创建实例并初始化所需的领域状态。
     * @param category category参数，参与本次操作；默认值为 `"all"`
     */
    constructor(public readonly category: "all" | "attack" | "status" = "all") {
        super();
    }
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description() {
        return "指定类别的技能无效。";
    }
    /**
     * applyEffect：执行对应的领域操作。
     * @param { action, skill } { action, skill }参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect({ action, skill }: Context) {
        if (!action || !skill) return false;
        if (this.category === "attack" && !(skill instanceof AttackSkill)) return false;
        if (this.category === "status" && !(skill instanceof StatusSkill)) return false;
        action.sealed = true;
        return true;
    }
}

/**
 * 闪避：封装效果声明、生命周期包装与具体业务效果。
 */
export class 闪避 extends BaseEffect {
    /**
     * perspective，保存当前实例的perspective状态。
     */
    readonly perspective = "opponent" as const;
    /**
     * 创建实例并初始化所需的领域状态。
     * @param rate rate参数，参与本次操作
     */
    constructor(public readonly rate: number) {
        super();
        if (!Number.isFinite(rate) || rate < 0 || rate > 1)
            throw new RangeError("闪避率必须在0到1之间");
    }
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description() {
        return `闪避率${this.rate * 100}%`;
    }
    /**
     * applyEffect：执行对应的领域操作。
     * @param { action, skill } { action, skill }参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect({ action, skill }: Context) {
        if (!action || skill?.sureHit) return false;
        action.evasionRates.push(this.rate);
        return true;
    }
}

/**
 * 命中效果失效：封装效果声明、生命周期包装与具体业务效果。
 */
export class 命中效果失效 extends BaseEffect {
    /**
     * perspective，保存当前实例的perspective状态。
     */
    readonly perspective = "opponent" as const;
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description() {
        return "使对方本次技能的命中附效失效，仍承受普通伤害。";
    }
    /**
     * applyEffect：执行对应的领域操作。
     * @param { action } { action }参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect({ action }: Context) {
        if (!action) return false;
        action.hitEffectsInvalid = true;
        return true;
    }
}

/** 本次对方普通攻击伤害的乘区减免，不污染下一次行动。 */
export class 减伤 extends BaseEffect {
    /**
     * perspective，保存当前实例的perspective状态。
     */
    readonly perspective = "opponent" as const;
    /**
     * 创建实例并初始化所需的领域状态。
     * @param percent percent参数，参与本次操作
     */
    constructor(public readonly percent: ContextNumber) {
        super();
        if (
            typeof percent === "number" &&
            (!Number.isFinite(percent) || percent < 0 || percent > 100)
        )
            throw new RangeError("减伤百分比必须在0到100之间");
    }
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description() {
        return typeof this.percent === "number"
            ? `受到的普通攻击伤害减少${this.percent}%`
            : "按当前条件减少普通攻击伤害。";
    }
    /**
     * describe：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    override describe(context: Context) {
        return `受到的普通攻击伤害减少${resolveContextNumber(this.percent, context)}%`;
    }
    /**
     * applyEffect：执行对应的领域操作。
     * @param context 本次结算上下文，包含双方精灵、赛尔及事件信息
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected applyEffect(context: Context) {
        /**
         * percent，供本模块相关规则使用。
         */
        const percent = resolveContextNumber(this.percent, context)!;
        if (!Number.isFinite(percent) || percent < 0 || percent > 100)
            throw new RangeError("减伤百分比必须在0到100之间");
        return percent;
    }
}

/**
 * 无视对手能力等级：封装效果声明、生命周期包装与具体业务效果。
 */
export class 无视对手能力等级 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description() {
        return "计算对手能力时无视其能力等级变化。";
    }
    /**
     * applyEffect：执行对应的领域操作。
     * @param { sbjPet } { sbjPet }参数，参与本次操作
     */
    protected applyEffect({ sbjPet }: Context) {
        sbjPet.opponentLevelRule = "ignore";
    }
}

/**
 * 视对手强化为弱化：封装效果声明、生命周期包装与具体业务效果。
 */
export class 视对手强化为弱化 extends BaseEffect {
    /**
     * 读取当前description。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get description() {
        return "计算对手能力时将其能力提升视为同级下降。";
    }
    /**
     * applyEffect：执行对应的领域操作。
     * @param { sbjPet } { sbjPet }参数，参与本次操作
     */
    protected applyEffect({ sbjPet }: Context) {
        sbjPet.opponentLevelRule = "invertPositive";
    }
}

/**
 * 保伤：造成的伤害不低于指定数值。
 */
export class 保伤 extends BaseEffect {
    constructor(public readonly base: ContextNumber) {
        super();
        if (typeof base === "number") this.validate(base);
    }

    private validate(value: number) {
        if (!Number.isFinite(value) || value < 0)
            throw new RangeError("保伤下限必须是非负有限数值");
        return value;
    }

    get description() {
        return typeof this.base === "number"
            ? `造成的普通攻击伤害不低于${Math.ceil(this.base)}。`
            : "按当前条件保证普通攻击伤害下限。";
    }

    override describe(context: Context) {
        return `造成的普通攻击伤害不低于${Math.ceil(this.validate(resolveContextNumber(this.base, context)!))}。`;
    }

    protected applyEffect(context: Context) {
        return this.validate(resolveContextNumber(this.base, context)!);
    }
}
